import { Box, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ResponsiveLine } from "@nivo/line";
import { ReactComponent as CircleIcon } from "components/Icons/circle.svg";
import { ReactComponent as LegendSeparator } from "components/Icons/legendSeparator.svg";
import { ReactComponent as OpenLoyaltyLogo } from "components/Icons/openLoyaltyIcon.svg";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { addDays, addMonths, addYears } from "date-fns";
import { useMessage } from "hooks/intl";
import { kebabCase } from "lodash";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import {
  AggregationType,
  AggregationTypeUnion,
  ChartId,
  LineChartData,
  LineChartId,
  TooltipData,
} from "types/charts";
import { OLDate } from "utilities/dateFormat";
import { abbreviateNumber, formatNumberValue } from "utilities/numbers";
import { checkDateRange } from "./LineChartWithOverview/utils";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    toolTip: {
      background: theme.primary800,
      borderRadius: "8px",
      boxShadow:
        "0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
      color: theme.white50,
      padding: theme.spacing(10),
    },
    date: {
      color: theme.primary200,
      fontSize: 14,
    },
    boldText: {
      fontWeight: "bold",
    },
    lineWrapper: ({ chartPadding }: { chartPadding: number }) => ({
      height: "354px",
      padding: theme.spacing(8, 8, chartPadding),
      "& svg": {
        overflow: "visible !important",
      },
    }),
    smallLineWrapper: {
      height: "294px",
      padding: theme.spacing(8, 8, 4),
      "& svg": {
        overflow: "visible !important",
      },
    },
    loaderContainer: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(12),
    },
    margin: {
      marginTop: theme.spacing(6),
    },
    chartLegend: {
      display: "flex",
      fontSize: 12,
      marginTop: theme.spacing(15),
      marginLeft: theme.spacing(10),
      flexWrap: "wrap",
    },
    chartLegendItem: {
      display: "flex",
      alignItems: "center",
      marginRight: theme.spacing(6),
      "& span": {
        marginLeft: theme.spacing(3),
      },
      "& > span": {
        whiteSpace: "nowrap",
      },
    },
    serieId: {
      fontSize: 12,
      display: "flex",
      alignItems: "baseline",
      margin: 0,
      wordBreak: "break-all",
      "& svg": {
        width: 8,
        height: 8,
        marginRight: theme.spacing(4),
      },
    },
    point: {
      marginTop: theme.spacing(6),
      maxWidth: 220,
      overflow: "hidden",
    },
    multilineTooltipDate: {
      color: theme.primary200,
      fontSize: 12,
    },
    legendSeparator: {
      marginLeft: theme.spacing(6),
    },
  }),
);

const WEEK_IN_DAYS = 7;

const DashedSolidLine = (props: any) => {
  return props.series.map(
    ({ id, data, color }: { id: LineChartId; data: any; color: string }) => (
      <path
        key={id}
        d={props.lineGenerator(
          data.map((d: any) => ({
            x: props.xScale(d.data.x),
            y: props.yScale(d.data.y),
          })),
        )}
        fill="none"
        stroke={color}
        style={{
          ...(id === ChartId.COMPARATIVE && { strokeDasharray: "6, 5" }),
          strokeWidth: 2,
        }}
      />
    ),
  );
};

export function LineChart({
  data,
  aggregationType,
  tooltip,
  showLegend,
}: {
  data: LineChartData[];
  aggregationType: AggregationTypeUnion;
  tooltip?: string;
  showLegend?: boolean;
}) {
  const { message } = useMessage();
  const refElement = useRef<HTMLDivElement>(null);
  const [legendContainerCurrentHeight, setLegendContainerCurrentHeight] =
    useState(0);

  const singleLineLegendHeight = 32;
  const isMultilegendHeight =
    legendContainerCurrentHeight > singleLineLegendHeight;
  const legendHeight = isMultilegendHeight
    ? legendContainerCurrentHeight
    : singleLineLegendHeight;

  const classes = useStyles({
    chartPadding: legendHeight,
  });

  const isMultilineChart = !!data?.filter((el) => el.multiline)?.length;

  const tooltipDataTestId =
    data && kebabCase(`${data[0]?.id} ${tooltip as string}`);

  const lineChartDataNotExist = data && data[0]?.data.length === 0;

  const { DAY, WEEK, MONTH, YEAR, QUARTER } = AggregationType;

  const getWeeklyDateRange = (x?: string | number) =>
    // 6 because the range ends the day before the next point.
    OLDate(addDays(moment(x, "DD-MM-YYYY").toDate() as Date, 6), {
      formatString: "dd-MM-yyyy",
    });

  const getMonthlyDateRange = (x?: string | number) =>
    OLDate(addMonths(moment(x, "MM-YYYY").toDate() as Date, 1), {
      formatString: "MM-yyyy",
    });

  const getYearlyDateRange = (x?: string | number) =>
    OLDate(addYears(moment(x, "YYYY").toDate() as Date, 1), {
      formatString: "yyyy",
    });

  const getXValue = (xValue?: string | number) => {
    switch (aggregationType) {
      case "week":
        return `${xValue} - ${getWeeklyDateRange(xValue)}`;
      case "month":
        return `${xValue} - ${getMonthlyDateRange(xValue)}`;
      case "year":
        return `${xValue} - ${getYearlyDateRange(xValue)}`;
      default:
        return xValue;
    }
  };

  const comparativeDataTooltip = (props: {
    slice: {
      points: readonly {
        data: TooltipData;
      }[];
    };
  }) => {
    const [mainData] = props.slice.points.filter(
      (point: any) => point.serieId === ChartId.MAIN,
    );
    const [comparativeData] = props.slice.points.filter(
      (point: any) => point.serieId === ChartId.COMPARATIVE,
    );

    const yValue = mainData?.data?.yFormatted;
    const xValue = getXValue(mainData?.data?.xFormatted);
    const comparativeXValue = getXValue(comparativeData?.data?.comparativeDate);
    const comparativeYValue = comparativeData?.data?.yFormatted;
    const isPercentageValue = mainData?.data?.isPercentageValue;

    return (
      <div
        className={classes.toolTip}
        data-test-id={`${tooltipDataTestId}-tooltip`}
      >
        <div className={classes.date}>{xValue}</div>
        <div>
          {tooltip && <>{message({ id: tooltip })}: </>}
          <span className={classes.boldText}>
            {formatNumberValue(yValue)}
            {isPercentageValue && "%"}
          </span>
        </div>
        {comparativeData && (
          <>
            <div className={`${classes.date} ${classes.margin}`}>
              {comparativeXValue}
            </div>
            <div>
              {tooltip && <>{message({ id: tooltip })}: </>}
              <span className={classes.boldText}>
                {formatNumberValue(comparativeYValue)}
                {isPercentageValue && "%"}
              </span>
            </div>
          </>
        )}
      </div>
    );
  };

  const multilineDataTooltip = (props: {
    slice: {
      points: readonly {
        data: TooltipData;
        serieColor: string;
        serieId: string | number;
        id: string;
      }[];
    };
  }) => {
    const xValue = getXValue(props?.slice?.points[0]?.data?.xFormatted);

    return (
      <div
        className={classes.toolTip}
        data-test-id={`${tooltipDataTestId}-tooltip`}
      >
        <div className={classes.multilineTooltipDate}>{xValue}</div>
        {props?.slice?.points?.map((point, index) => {
          return (
            <div className={classes.point} key={index}>
              <p className={classes.serieId}>
                <CircleIcon style={{ color: point?.serieColor }} />{" "}
                {point?.serieId}
              </p>
              <div>
                {tooltip && <>{message({ id: tooltip })}: </>}
                <span className={classes.boldText}>
                  {formatNumberValue(point?.data?.yFormatted)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const olTheme: OLTheme = useTheme();
  const lineTheme = {
    crosshair: {
      line: {
        stroke: olTheme.gray100,
        strokeWidth: 1,
        strokeDasharray: "0",
      },
    },
    grid: {
      line: {
        stroke: olTheme.gray100,
        strokeWidth: 1,
      },
    },
    axis: {
      ticks: {
        line: {
          stroke: olTheme.gray400,
        },
        text: {
          fill: olTheme.gray400,
          fontSize: 12,
        },
      },
    },
  };

  // Get y axis max value for nivo
  const getMaxValue = (preppedData: any[]) => {
    const maxArray: number[] = preppedData.map((data) =>
      data?.data.reduce(
        (max: number, p: { y: number }) => (p?.y > max ? p?.y : max),
        data?.data[0]?.y,
      ),
    );

    // Chart can contain more than one dataset - get the highest value from the available datasets
    const maxValue = Math.max(...maxArray);

    if (maxValue === 0) return 1;
    let padding;
    if (maxValue <= 10) {
      padding = 10;
    } else if (maxValue > 10 && maxValue <= 1000) {
      padding = 100;
    } else {
      padding = 1000;
    }
    return (
      Math.ceil(
        maxArray.reduce((max, p) => (p > max ? p : max), maxArray[0]) / padding,
      ) * padding
    );
  };

  const formatDate = (interval: AggregationTypeUnion) => {
    switch (interval) {
      case DAY:
      case WEEK:
        return "%d-%m-%Y";
      case MONTH:
      case QUARTER:
        return "%m-%Y";
      case YEAR:
        return "%Y";
      default:
        return "%d-%m-%Y";
    }
  };

  const getDateRange = () => {
    const axisData = data && data[0]?.data;

    const startDate = axisData[0]?.x as string;
    const endDate = axisData[axisData.length - 1]?.x as string;

    const dateRange = checkDateRange(startDate, endDate);

    return dateRange;
  };

  const setTickValues = (interval: AggregationTypeUnion) => {
    if (lineChartDataNotExist) {
      return;
    }
    switch (interval) {
      case DAY:
        const { days, months } = getDateRange();
        return (months && months > 0) || (days && days >= WEEK_IN_DAYS)
          ? "every 7 day"
          : "every 1 days";
      case WEEK:
        return "every 7 days";
      case MONTH:
        return "every month";
      case QUARTER:
        return "every 3 months";
      case YEAR:
        return "every year";
      default:
        return "every 7 days";
    }
  };

  useEffect(() => {
    const currentRefElement = refElement.current;

    if (!currentRefElement || !isMultilineChart) return;

    const resizeObserver = new ResizeObserver(() => {
      setLegendContainerCurrentHeight(
        currentRefElement?.clientHeight as number,
      );
    });

    resizeObserver.observe(currentRefElement);

    return () => resizeObserver.disconnect();
  }, [isMultilineChart]);

  return (
    <div
      className={showLegend ? classes.lineWrapper : classes.smallLineWrapper}
      id="chart"
    >
      {data && aggregationType ? (
        <>
          <ResponsiveLine
            axisBottom={{
              format: formatDate(aggregationType),
              tickPadding: 5,
              tickSize: 5,
              tickValues: setTickValues(aggregationType),
            }}
            axisLeft={null}
            axisRight={{
              tickPadding: 5,
              tickRotation: 0,
              tickSize: 5,
              tickValues: 4,
              format: (value) => abbreviateNumber(value),
            }}
            areaBaselineValue={0}
            areaBlendMode="normal"
            areaOpacity={0.2}
            curve="linear"
            debugMesh={false}
            debugSlices={false}
            defs={[]}
            enableArea={false}
            enableCrosshair={true}
            enableGridY={true}
            enablePointLabel={false}
            enablePoints={true}
            fill={[]}
            isInteractive={true}
            legends={[]}
            lineWidth={2}
            pointLabel=""
            role=""
            tooltip={() => <></>}
            axisTop={null}
            colors={(d) => d.color}
            crosshairType="x"
            data={data}
            enableGridX={false}
            enableSlices="x"
            margin={{
              bottom: 24,
              left: 24,
              right: 54,
              top: 24,
            }}
            pointBorderColor={{ from: "serieColor" }}
            pointBorderWidth={3}
            pointColor={{ theme: "background" }}
            pointLabelYOffset={-12}
            pointSize={3}
            sliceTooltip={
              isMultilineChart ? multilineDataTooltip : comparativeDataTooltip
            }
            theme={lineTheme}
            useMesh={true}
            xScale={{
              type: "time",
              format: formatDate(aggregationType),
            }}
            xFormat={`time:${formatDate(aggregationType)}`}
            yScale={{
              max: getMaxValue(data),
              min: "auto",
              reverse: false,
              type: "linear",
            }}
            layers={[
              "grid",
              "markers",
              "axes",
              "areas",
              "crosshair",
              "slices",
              "points",
              "mesh",
              "legends",
              DashedSolidLine,
            ]}
          />
          {showLegend && (
            <div
              className={classes.chartLegend}
              ref={isMultilineChart ? refElement : null}
            >
              {data?.map((item, index) => {
                if (!item.legendLabel) {
                  return null;
                }
                const maxLegendItemLength = 12;
                const disableTooltip =
                  item.legendLabel.length < maxLegendItemLength ||
                  !isMultilineChart;
                const shortenLegendLabel = `${item?.legendLabel?.substring(0, maxLegendItemLength)}...`;
                const isLegenItemShortened =
                  item?.legendLabel?.length > maxLegendItemLength &&
                  isMultilineChart;
                const isSeparatorVisible =
                  isMultilineChart &&
                  data?.length > 1 &&
                  index + 1 < data.length;

                return (
                  item.legendLabel && (
                    <div className={classes.chartLegendItem} key={item.id}>
                      {item.legendIcon}{" "}
                      <Tooltip
                        text={item.legendLabel}
                        disableHoverListener={disableTooltip}
                      >
                        <span>
                          {isLegenItemShortened
                            ? shortenLegendLabel
                            : item.legendLabel}
                        </span>
                      </Tooltip>
                      {isSeparatorVisible && (
                        <LegendSeparator className={classes.legendSeparator} />
                      )}
                    </div>
                  )
                );
              })}
            </div>
          )}
        </>
      ) : (
        <Box className={classes.loaderContainer}>
          <OpenLoyaltyLogo />
          <CircularProgress />
        </Box>
      )}
    </div>
  );
}
